export interface AudioDevicesSnapshot {
  microphoneId: string | null;
  microphoneLabel: string | null;
  speakerId: string | null;
  speakerLabel: string | null;
  microphoneCount: number;
  speakerCount: number;
}

export interface AudioDevicesSnapshotOptions {
  /** The active audio track from an ongoing call, used to get the actual device being used */
  activeAudioTrack?: MediaStreamTrack | null;
}

export async function getAudioDevicesSnapshot(
  options?: AudioDevicesSnapshotOptions,
): Promise<AudioDevicesSnapshot> {
  const speakerId = localStorage.getItem('selectedSpeaker');

  // Get microphoneId from active track if available, otherwise fall back to localStorage
  let microphoneId: string | null = null;
  if (options?.activeAudioTrack) {
    const settings = options.activeAudioTrack.getSettings();
    microphoneId = settings.deviceId ?? null;
  } else {
    microphoneId = localStorage.getItem('selectedMicrophone');
  }

  let microphoneLabel: string | null = null;
  let speakerLabel: string | null = null;
  let microphoneCount = 0;
  let speakerCount = 0;

  try {
    const devices = await navigator.mediaDevices.enumerateDevices();

    const microphones = devices.filter((device) => device.kind === 'audioinput');
    const speakers = devices.filter((device) => device.kind === 'audiooutput');

    microphoneCount = microphones.length;
    speakerCount = speakers.length;

    // Cache for sync snapshot
    lastKnownMicrophoneCount = microphoneCount;
    lastKnownSpeakerCount = speakerCount;

    if (microphoneId) {
      const selectedMic = microphones.find((device) => device.deviceId === microphoneId);
      microphoneLabel = selectedMic?.label || null;
    }

    if (speakerId) {
      const selectedSpeaker = speakers.find((device) => device.deviceId === speakerId);
      speakerLabel = selectedSpeaker?.label || null;
    }
  } catch {
    // If we can't enumerate devices, return what we have from localStorage
  }

  return {
    microphoneId,
    microphoneLabel,
    speakerId,
    speakerLabel,
    microphoneCount,
    speakerCount,
  };
}

/** Cached counts from the last async snapshot, used by the sync variant */
let lastKnownMicrophoneCount: number | null = null;
let lastKnownSpeakerCount: number | null = null;

export function getAudioDevicesSnapshotSync(): AudioDevicesSnapshot {
  return {
    microphoneId: localStorage.getItem('selectedMicrophone'),
    microphoneLabel: null,
    speakerId: localStorage.getItem('selectedSpeaker'),
    speakerLabel: null,
    microphoneCount: lastKnownMicrophoneCount ?? -1,
    speakerCount: lastKnownSpeakerCount ?? -1,
  };
}
